
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/use-language';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  subgroupCount: number;
  upcomingEvents: number;
  isParent: boolean;
  parentGroup?: string;
}

interface SubgroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  subgroup?: Group;
  parentGroups: Group[];
  onSubmit: (subgroupData: Partial<Group>) => void;
}

export function SubgroupFormDialog({
  open,
  onOpenChange,
  mode,
  subgroup,
  parentGroups,
  onSubmit
}: SubgroupFormDialogProps) {
  const { toast } = useToast();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentGroup: '',
    isParent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === 'edit' && subgroup) {
      setFormData({
        name: subgroup.name,
        description: subgroup.description,
        parentGroup: subgroup.parentGroup || '',
        isParent: subgroup.isParent
      });
    } else {
      setFormData({
        name: '',
        description: '',
        parentGroup: 'Main Padel Tennis Group',
        isParent: false
      });
    }
    setErrors({});
  }, [mode, subgroup, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (mode === 'create' && !formData.isParent && !formData.name.includes(' / ')) {
      newErrors.name = 'Please use format "Norwegian / English" for subgroups';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const groupData: Partial<Group> = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      parentGroup: formData.parentGroup === 'none' ? undefined : formData.parentGroup || undefined,
      isParent: formData.isParent,
      memberCount: mode === 'edit' ? subgroup?.memberCount : 0,
      subgroupCount: mode === 'edit' ? subgroup?.subgroupCount : 0,
      upcomingEvents: mode === 'edit' ? subgroup?.upcomingEvents : 0,
    };

    if (mode === 'edit' && subgroup) {
      groupData.id = subgroup.id;
    }

    onSubmit(groupData);
    onOpenChange(false);
    
    toast({
      title: mode === 'create' ? 'Group Created' : 'Group Updated',
      description: `${formData.name} has been ${mode === 'create' ? 'created' : 'updated'} successfully.`
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Group' : 'Edit Group'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder={formData.isParent ? "e.g., Tennis Group" : "e.g., Nybegynner / Beginner"}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the purpose and target audience of this group..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={errors.description ? 'border-destructive' : ''}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">
              {mode === 'create' ? 'Create Group' : 'Update Group'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
